import { useState } from "react";
import {
  Settings as SettingsIcon,
  Shield,
  Bell,
  Cpu,
  Save,
  UploadCloud,
  Globe,
  Mail,
  Lock,
  Key,
  Eye,
  Zap,
  AlertCircle,
  CheckCircle2,
  Smartphone,
  Monitor,
  Database,
  Clock,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@repo/core-ui";

const TabButton = ({ active, icon: Icon, label, onClick }: any) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 relative overflow-hidden ${
      active
        ? "bg-blue-600 text-white shadow-sm"
        : "text-slate-500 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-[#1b254b] hover:text-slate-800 dark:hover:text-slate-200"
    }`}
  >
    <Icon size={16} />
    {label}
    {active && (
      <motion.div
        layoutId="activeTab"
        className="absolute inset-0 bg-blue-600 -z-10"
      />
    )}
  </button>
);

const SettingToggle = ({ label, description, enabled, icon: Icon }: any) => (
  <div className="flex items-center justify-between p-5 rounded-2xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 hover:border-blue-200 dark:hover:border-white/20 transition-colors group">
    <div className="flex items-center gap-4">
      <div className="p-2.5 bg-white dark:bg-[#1b254b] rounded-xl border border-slate-200 dark:border-white/5 text-slate-400 group-hover:text-blue-600 group-hover:bg-blue-50 dark:group-hover:bg-white/10 transition-colors">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
          {label}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
          {description}
        </p>
      </div>
    </div>
    <button
      className={`w-11 h-6 rounded-full transition-all duration-300 relative ${enabled ? "bg-blue-600" : "bg-slate-200 dark:bg-[#1b254b]"}`}
    >
      <div
        className={`absolute top-1 w-4 h-4 bg-white dark:bg-slate-100 rounded-full transition-all duration-300 ${enabled ? "left-6" : "left-1"}`}
      />
    </button>
  </div>
);

const Settings = () => {
  const [activeTab, setActiveTab] = useState("general");

  return (
    <div className="space-y-8 animate-fadeIn min-h-screen pb-10">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white dark:bg-[#111c44] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-bold text-xs uppercase tracking-widest">
            <SettingsIcon size={14} fill="currentColor" />
            Platform Configuration
          </div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
            System Settings
          </h1>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
            Manage global configurations, security protocols, and system health.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-all shadow-sm">
            <Save size={16} />
            Save Changes
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap items-center gap-2 p-1.5 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm w-fit max-w-full overflow-x-auto">
        <TabButton
          active={activeTab === "general"}
          icon={Globe}
          label="General"
          onClick={() => setActiveTab("general")}
        />
        <TabButton
          active={activeTab === "security"}
          icon={Shield}
          label="Security"
          onClick={() => setActiveTab("security")}
        />
        <TabButton
          active={activeTab === "notifications"}
          icon={Bell}
          label="Notifications"
          onClick={() => setActiveTab("notifications")}
        />
        <TabButton
          active={activeTab === "system"}
          icon={Cpu}
          label="System"
          onClick={() => setActiveTab("system")}
        />
      </div>

      {/* Content Area */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="grid gap-6"
        >
          {activeTab === "general" && (
            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 p-8 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm space-y-8">
                <section className="space-y-5">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3">
                    <div className="w-1.5 h-6 bg-blue-600 rounded-full" />
                    Platform Identity
                  </h3>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 pl-1">
                        Platform Name
                      </label>
                      <input
                        type="text"
                        defaultValue="RoooMix Global"
                        className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/5 rounded-xl px-4 py-3 font-medium text-sm text-slate-800 dark:text-slate-200 outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 pl-1">
                        Support Email
                      </label>
                      <input
                        type="email"
                        defaultValue="ops@rooomix.com"
                        className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/5 rounded-xl px-4 py-3 font-medium text-sm text-slate-800 dark:text-slate-200 outline-none transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 pl-1">
                      Legal Address
                    </label>
                    <textarea
                      rows={3}
                      defaultValue="Level 24, One World Center, Lower Parel, Mumbai, Maharashtra 400013"
                      className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/5 rounded-xl px-4 py-3 font-medium text-sm text-slate-800 dark:text-slate-200 outline-none transition-all resize-none"
                    />
                  </div>
                </section>

                <section className="space-y-5">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3">
                    <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                    Regional Settings
                  </h3>
                  <div className="grid md:grid-cols-3 gap-5">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 pl-1">
                        Default Timezone
                      </label>
                      <select className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/5 rounded-xl px-4 py-3 font-medium text-sm text-slate-800 dark:text-slate-200 outline-none transition-all appearance-none cursor-pointer">
                        <option>IST (UTC +5:30)</option>
                        <option>UTC (GMT 0)</option>
                        <option>EST (UTC -5:00)</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 pl-1">
                        Default Currency
                      </label>
                      <select className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/5 rounded-xl px-4 py-3 font-medium text-sm text-slate-800 dark:text-slate-200 outline-none transition-all appearance-none cursor-pointer">
                        <option>INR (₹)</option>
                        <option>USD ($)</option>
                        <option>EUR (€)</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 pl-1">
                        System Language
                      </label>
                      <select className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/5 rounded-xl px-4 py-3 font-medium text-sm text-slate-800 dark:text-slate-200 outline-none transition-all appearance-none cursor-pointer">
                        <option>English (Global)</option>
                        <option>Hindi (Localized)</option>
                        <option>Spanish</option>
                      </select>
                    </div>
                  </div>
                </section>
              </Card>

              <Card className="p-8 bg-slate-900 dark:bg-slate-950 rounded-2xl border-none shadow-lg shadow-slate-900/10 space-y-6 flex flex-col items-center justify-center text-center">
                <div className="relative group">
                  <div className="w-28 h-28 rounded-2xl bg-white/5 flex items-center justify-center border-2 border-dashed border-white/20 group-hover:border-blue-400 group-hover:bg-white/10 transition-all cursor-pointer">
                    <UploadCloud
                      className="text-white/40 group-hover:text-blue-400 transition-all"
                      size={36}
                    />
                  </div>
                  <div className="absolute -bottom-3 -right-3 w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center border-4 border-slate-900 dark:border-slate-950 shadow-sm">
                    <Zap size={16} className="text-white" />
                  </div>
                </div>
                <div className="space-y-1">
                  <h4 className="text-lg font-bold text-white tracking-tight">
                    Platform Logo
                  </h4>
                  <p className="text-slate-400 text-xs font-medium">
                    PNG or SVG format. Max size 2MB.
                  </p>
                </div>
                <div className="w-full space-y-2.5 pt-4">
                  <button className="w-full py-3 rounded-xl bg-white text-slate-800 font-bold text-sm hover:bg-slate-50 transition-colors shadow-sm">
                    Replace Logo
                  </button>
                  <button className="w-full py-3 rounded-xl text-rose-400 font-semibold text-sm hover:bg-rose-500/10 transition-colors">
                    Remove Branding
                  </button>
                </div>
              </Card>
            </div>
          )}

          {activeTab === "security" && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-8 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm space-y-6">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3">
                    <Lock size={20} className="text-indigo-600" />
                    Access Controls
                  </h3>
                  <div className="space-y-4">
                    <SettingToggle
                      label="Two-Factor Authentication"
                      description="Require MFA for all super admin logins."
                      enabled={true}
                      icon={Smartphone}
                    />
                    <SettingToggle
                      label="IP Whitelisting"
                      description="Restrict dashboard access to office IPs."
                      enabled={false}
                      icon={Monitor}
                    />
                    <SettingToggle
                      label="Session Timeout"
                      description="Auto logout after 30 mins of inactivity."
                      enabled={true}
                      icon={Clock}
                    />
                  </div>
                </Card>

                <Card className="p-8 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm space-y-6">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3">
                    <Key size={20} className="text-blue-600" />
                    Password Policy
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: "Minimum Length", value: "12 chars" },
                      { label: "Update Frequency", value: "Every 90 days" },
                      {
                        label: "Complexity Requirements",
                        value: "Symbols + Caps",
                      },
                    ].map((policy, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 hover:border-blue-200 dark:hover:border-white/20 transition-colors"
                      >
                        <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                          {policy.label}
                        </span>
                        <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
                          {policy.value}
                        </span>
                      </div>
                    ))}
                    <button className="w-full py-3 rounded-xl bg-blue-50 dark:bg-white/5 text-blue-600 dark:text-blue-400 font-bold text-sm hover:bg-blue-100 dark:hover:bg-white/10 transition-colors mt-2">
                      Enforce Global Reset
                    </button>
                  </div>
                </Card>
              </div>

              <Card className="p-8 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                    Recent Security Activity
                  </h3>
                  <button className="text-sm font-semibold text-blue-600 hover:underline">
                    Full Audit Log
                  </button>
                </div>
                <div className="space-y-3">
                  {[
                    {
                      event: "Login Attempt Blocked",
                      ip: "192.168.1.42",
                      location: "Moscow, RU",
                      status: "Critical",
                      time: "10 mins ago",
                    },
                    {
                      event: "Database Backup Downloaded",
                      ip: "10.0.0.12",
                      location: "Mumbai, IN",
                      status: "Warning",
                      time: "2 hours ago",
                    },
                    {
                      event: "MFA Setting Modified",
                      ip: "10.0.0.8",
                      location: "Delhi, IN",
                      status: "Info",
                      time: "5 hours ago",
                    },
                  ].map((log, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-100 dark:border-white/5 hover:border-slate-200 dark:hover:border-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`p-2 rounded-lg ${
                            log.status === "Critical"
                              ? "bg-rose-100 text-rose-600"
                              : log.status === "Warning"
                                ? "bg-amber-100 text-amber-600"
                                : "bg-blue-100 text-blue-600"
                          }`}
                        >
                          <AlertCircle size={16} />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                            {log.event}
                          </p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium tracking-wide mt-0.5">
                            {log.ip} • {log.location}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">
                        {log.time}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {activeTab === "notifications" && (
            <Card className="p-8 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm space-y-8">
              <div className="grid md:grid-cols-2 gap-10">
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3">
                    <Mail size={20} className="text-emerald-500" />
                    Email Digests
                  </h3>
                  <div className="space-y-4">
                    <SettingToggle
                      label="Daily Revenue Report"
                      description="Summary of platform financial health."
                      enabled={true}
                      icon={Mail}
                    />
                    <SettingToggle
                      label="Critical Alerts"
                      description="Immediate emails for system failures."
                      enabled={true}
                      icon={Mail}
                    />
                    <SettingToggle
                      label="Weekly Insight"
                      description="Analytical trends and growth metrics."
                      enabled={false}
                      icon={Mail}
                    />
                  </div>
                </div>
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3">
                    <Smartphone size={20} className="text-blue-600" />
                    Push Notifications
                  </h3>
                  <div className="space-y-4">
                    <SettingToggle
                      label="New Hotel Registration"
                      description="Real-time alert for new property signup."
                      enabled={true}
                      icon={Smartphone}
                    />
                    <SettingToggle
                      label="Staff Activity"
                      description="Alerts for significant staff actions."
                      enabled={false}
                      icon={Smartphone}
                    />
                    <SettingToggle
                      label="Maintenance Updates"
                      description="Notifications for scheduled downtime."
                      enabled={true}
                      icon={Smartphone}
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === "system" && (
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="md:col-span-2 p-8 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm space-y-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between p-6 rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-900/30 group gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-rose-600 rounded-xl text-white shadow-sm group-hover:rotate-12 transition-all duration-300 shrink-0">
                      <AlertCircle size={24} />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-slate-800 dark:text-slate-100 tracking-tight">
                        Maintenance Mode
                      </h4>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400 max-w-sm mt-1">
                        Suspend platform access for all users except assigned
                        staff during updates.
                      </p>
                    </div>
                  </div>
                  <button className="px-6 py-3 bg-rose-600 text-white rounded-xl font-bold shadow-sm hover:bg-rose-700 focus:ring-4 focus:ring-rose-500/20 active:scale-95 transition-all whitespace-nowrap">
                    Enable Mode
                  </button>
                </div>

                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3">
                    <Key size={20} className="text-amber-500" />
                    API Management
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      {
                        service: "Payment Gateway",
                        key: "rk_live_••••••••",
                        status: "Active",
                      },
                      {
                        service: "Email SMTP",
                        key: "smtp_••••••••",
                        status: "Active",
                      },
                      {
                        service: "Cloud Storage",
                        key: "aws_••••••••",
                        status: "Active",
                      },
                      {
                        service: "Maps API",
                        key: "gms_••••••••",
                        status: "Quota Warning",
                      },
                    ].map((api, i) => (
                      <div
                        key={i}
                        className="p-5 rounded-2xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 hover:border-blue-300 dark:hover:border-white/20 transition-colors flex items-center justify-between"
                      >
                        <div className="space-y-1">
                          <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                            {api.service}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 font-mono">
                              {api.key}
                            </span>
                            <Eye
                              size={14}
                              className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors"
                            />
                          </div>
                        </div>
                        <div
                          className={`w-2 h-2 rounded-full ${api.status === "Active" ? "bg-emerald-500" : "bg-amber-500"}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              <div className="space-y-6">
                <Card className="p-8 bg-slate-900 rounded-2xl border-none text-white space-y-6 overflow-hidden relative shadow-lg shadow-slate-900/10">
                  <div className="absolute top-0 right-0 p-6 opacity-10">
                    <Database size={80} />
                  </div>
                  <div className="relative">
                    <h4 className="text-base font-bold tracking-tight text-white/90">
                      Database Health
                    </h4>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-white">94%</span>
                      <span className="text-white/60 font-semibold text-sm">
                        Optimization
                      </span>
                    </div>
                    <div className="mt-6 h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: "94%" }}
                        className="h-full bg-blue-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                      />
                    </div>
                    <p className="mt-5 text-xs text-white/50 font-medium leading-relaxed">
                      Index efficiency is at maximum. Next auto-vacuum scheduled
                      in 6 hours.
                    </p>
                  </div>
                </Card>

                <Card className="p-6 bg-white dark:bg-[#111c44] border border-slate-200 dark:border-white/10 shadow-sm space-y-5">
                  <h4 className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                    System Info
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400 font-medium">
                        Core Version
                      </span>
                      <span className="font-bold text-slate-800 dark:text-slate-100">
                        v4.8.2-stable
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400 font-medium">
                        Cluster Nodes
                      </span>
                      <span className="font-bold text-slate-800 dark:text-slate-100">
                        08 Active
                      </span>
                    </div>
                    <button className="w-full mt-2 py-3 text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-transparent hover:border-blue-100 dark:hover:border-blue-800 rounded-xl transition-all flex items-center justify-center gap-2">
                      <CheckCircle2 size={16} />
                      Update Available
                    </button>
                  </div>
                </Card>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Settings;
